import { useMemo } from 'react';
import { InfiniteData, useQueryClient } from '@tanstack/react-query';

import { ArticleComment } from '@web/newsfeed/types/Article';
import { getArticleCommentsQueryKey } from '@web/newsfeed/utils/getQueryKey';
import { useNewsfeedQueryCacheMutations } from '@web/newsfeed/hooks/useNewsfeedQueryCacheMutations';

type RevertCallback = () => void;
export const useArticleCommentsQueryCache = () => {
  const queryClient = useQueryClient();
  const { onAddArticleComment } = useNewsfeedQueryCacheMutations();

  return useMemo(() => {
    const addComment = (comment: ArticleComment) => {
      const queryKey = getArticleCommentsQueryKey(comment.articleId);

      queryClient.setQueryData<InfiniteData<ArticleComment[]>>(queryKey, (cachedData) => {
        if (!cachedData) return;

        const { pages } = cachedData;
        const newestPageIndex = pages.length - 1;
        const newestPageComments = pages[newestPageIndex];
        const updatedPage = [...newestPageComments, comment];
        const updatedPages = [...pages.slice(0, newestPageIndex), updatedPage];

        return {
          ...cachedData,
          pages: updatedPages,
        };
      });

      onAddArticleComment(comment.articleId);
    };

    const addCommentLike = (articleId: ID, commentId: ID) => {
      const queryKey = getArticleCommentsQueryKey(articleId);

      queryClient.setQueryData<InfiniteData<ArticleComment[]>>(queryKey, (cachedData) => {
        if (!cachedData) return;

        return {
          ...cachedData,
          pages: cachedData.pages.map((comments) =>
            comments.map(
              (comment): ArticleComment =>
                comment.id === commentId
                  ? { ...comment, likesCount: (comment.likesCount || 0) + 1, likedByMe: true }
                  : comment
            )
          ),
        };
      });
    };

    const deleteComment = (articleId: ID, commentId: ID): RevertCallback => {
      const setDeleted = async (isDeleted: boolean) => {
        const queryKey = getArticleCommentsQueryKey(articleId);
        await queryClient.cancelQueries(queryKey);

        queryClient.setQueryData<InfiniteData<ArticleComment[]>>(queryKey, (cachedData) => {
          if (!cachedData) return;

          return {
            ...cachedData,
            pages: cachedData.pages.map((comments) =>
              comments.map((comment) =>
                comment.id === commentId ? { ...comment, isDeleted } : comment
              )
            ),
          };
        });
      };

      void setDeleted(true);
      return () => {
        void setDeleted(false);
        onAddArticleComment(articleId);
      };
    };

    const editComment = (updatedComment: ArticleComment) => {
      const queryKey = getArticleCommentsQueryKey(updatedComment.articleId);

      queryClient.setQueryData<InfiniteData<ArticleComment[]>>(queryKey, (cachedData) => {
        if (!cachedData) return;

        return {
          ...cachedData,
          pages: cachedData.pages.map((comments) =>
            comments.map(
              (comment): ArticleComment =>
                comment.id === updatedComment.id ? { ...updatedComment } : comment
            )
          ),
        };
      });
    };

    const removeCommentLike = (articleId: ID, commentId: ID) => {
      const queryKey = getArticleCommentsQueryKey(articleId);

      queryClient.setQueryData<InfiniteData<ArticleComment[]>>(queryKey, (cachedData) => {
        if (!cachedData) return;

        return {
          ...cachedData,
          pages: cachedData.pages.map((comments) =>
            comments.map(
              (comment): ArticleComment =>
                comment.id === commentId
                  ? {
                      ...comment,
                      likesCount: comment.likesCount > 0 ? comment.likesCount - 1 : 0,
                      likedByMe: false,
                    }
                  : comment
            )
          ),
        };
      });
    };

    return {
      addComment,
      addCommentLike,
      deleteComment,
      editComment,
      removeCommentLike,
    };
  }, [queryClient, onAddArticleComment]);
};
